import React from 'react';
import classnames from 'shared/utils/classnames';
import htmlTitle from 'shared/utils/htmlTitle';
import { CONTENT_TYPES } from 'shared/constants/survey';
import { BENCHMARK_CATEGORIES } from 'shared/constants/benchmark';
import { Button, Divider, Tag, Tooltip } from 'antd';
import ContentTypeIcon from 'components/ContentTypeIcon';
import Dependencies from 'components/Dependencies';
import { hasCategory } from 'shared/utils/categories';

import {
  PlusOutlined, MinusOutlined, DeleteFilled, DashboardOutlined,
} from '@ant-design/icons';
import useUser from 'hooks/useUser';
import { SUPER_USER } from 'shared/constants/permissions';
import DragHandle from 'components/DragHandle';

const SurveyContentTreeNodeItem = ({ onEdit, onRemove, ...rstProps }) => {
  const { hasPermission } = useUser();
  const {
    node,
    scaffoldBlockPxWidth,
    toggleChildrenVisibility,
    connectDragPreview,
    connectDragSource,
    isDragging,
    canDrop,
    didDrop,
    path,
    treeIndex,
  } = rstProps;

  let title = '';
  let type = '';

  if (node.block) {
    type = CONTENT_TYPES.BLOCK;
    title = node.block.title;
  } else if (node.question) {
    type = node.question.settings?.type || CONTENT_TYPES.QUESTION;
    const isRequired = node.question.settings?.required;
    title = <span dangerouslySetInnerHTML={htmlTitle(node.question.title, isRequired)} />;
  } else if (node.pageBreak) {
    type = CONTENT_TYPES.PAGE_BREAK;
  } else if (node.text) {
    type = CONTENT_TYPES.TEXT;
    title = node.text.title;
  }

  const rstRowClasses = classnames(
    'rst__row',
    isDragging && !didDrop && 'rst__rowLandingPad',
    isDragging && !didDrop && !canDrop && 'rst__rowCancelPad',
  );

  const hasChildren = (node.children && (node.children.length > 0 || typeof node.children === 'function'));

  const isActive = node.active || node.pageBreak;
  const CollapseIcon = node.expanded ? MinusOutlined : PlusOutlined;
  const benchmark = hasCategory(node.question?.categories, BENCHMARK_CATEGORIES);

  const canEdit = !benchmark || hasPermission(SUPER_USER);

  return connectDragPreview(
    <div className={`content content--${type} ${!isActive ? 'content--disabled' : ''}`}>
      {toggleChildrenVisibility && hasChildren && (
        <Button
          size="small"
          className="content__collapse"
          style={{ left: -0.5 * scaffoldBlockPxWidth }}
          icon={<CollapseIcon />}
          onClick={() => toggleChildrenVisibility({ node, path, treeIndex })}
        />
      )}
      {connectDragSource(
        <div className={rstRowClasses}>
          <DragHandle />
          {node.pageBreak ? (
            <Divider dashed className="content--PAGE-BREAK-line" />
          ) : (
            <>
              <Button
                type="link"
                className="content__edit"
                onClick={() => onEdit(node, path)}
                icon={<ContentTypeIcon type={type} />}
                disabled={!canEdit}
              >
                <span className="title">{title}</span>
              </Button>
              <Dependencies
                dependencies={node.dependencies}
                dependencyOperator={node.dependencyOperator}
              />
              {benchmark && (
                <Tooltip title="This question is part of a benchmark index and can't be modified.">
                  <Tag icon={<DashboardOutlined />}>{benchmark}</Tag>
                </Tooltip>
              )}
            </>
          )}
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteFilled />}
            onClick={() => onRemove(node)}
          />
        </div>,
        { dropEffect: 'copy' },
      )}
    </div>,
  );
};

export default SurveyContentTreeNodeItem;
